import { Flex } from '@chakra-ui/react'
import { CardRanking, Company, SearchPost } from 'components/molecules'
import { AIAdviceCompany } from 'components/molecules/AIAdviceCompany'
import WordMap from 'components/molecules/WordMap'
import {
  COMPANY_DETAIL_FEATURES,
  CompanyDetailFeature,
  PageNumberByTenant,
  toTenantCode,
} from 'constant/conmon'
import {
  tenantCompanyDetailSettingsSelector,
  tenantSelector,
} from 'context/Auth/auth.selector'
import { FC } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

/**
 * Props for TopSection component
 */
interface TopSectionProps {
  isRankingVisible: boolean
  isAIAdvisorVisible: boolean
  companyId?: string
}

/**
 * Top section component that renders ranking and AI advisor based on visibility
 */
const TopSection: FC<TopSectionProps> = ({
  isRankingVisible,
  isAIAdvisorVisible,
  companyId,
}) => {
  const shouldShow = isRankingVisible || isAIAdvisorVisible

  if (!shouldShow) return null

  const getAIAdvisorWidth = (): string => {
    return isRankingVisible ? '50%' : '100%'
  }

  return (
    <Flex gap={4} h="272px" w="100%">
      {isRankingVisible && (
        <CardRanking id={companyId} showScoreChange={true} width="50%" />
      )}
      {isAIAdvisorVisible && <AIAdviceCompany width={getAIAdvisorWidth()} />}
    </Flex>
  )
}

/**
 * CompanyDetailContainer organisms
 * Main container component for company detail page
 * @constructor
 */
export const CompanyDetailContainer: FC = () => {
  const { id } = useParams()
  const tenant = useSelector(tenantSelector)
  const companyDetailSettings = useSelector(tenantCompanyDetailSettingsSelector)

  /**
   * Check if a company detail feature should be hidden based on tenant settings
   * @param feature - The feature identifier to check
   * @returns true if feature should be shown, false if hidden
   */
  const isFeatureVisible = (feature: CompanyDetailFeature): boolean => {
    const hiddenFeatures = companyDetailSettings?.hidden_features || []
    return !hiddenFeatures.includes(feature)
  }

  // Get default page size from company detail settings or fallback to tenant default
  const perPageTable =
    companyDetailSettings?.items_per_page ??
    PageNumberByTenant[toTenantCode(tenant)]

  // Memoize feature visibility checks to avoid repeated calculations
  const isRankingVisible = isFeatureVisible(
    COMPANY_DETAIL_FEATURES.TRANSACTION_POSSIBILITY_RANKING,
  )
  const isAIAdvisorVisible = isFeatureVisible(
    COMPANY_DETAIL_FEATURES.AI_ADVISOR,
  )

  /**
   * Render the main content section with conditional features
   * @returns JSX element containing top section and word map
   */
  const renderContentByUser = () => {
    return (
      <>
        <TopSection
          companyId={id}
          isAIAdvisorVisible={isAIAdvisorVisible}
          isRankingVisible={isRankingVisible}
        />
        <WordMap />
      </>
    )
  }
  return (
    <Flex direction="column" gap="16px" w="100%">
      <Company />
      <SearchPost
        isDetailCompany={true}
        page="company"
        perPageTable={perPageTable}
      />
      {renderContentByUser()}
    </Flex>
  )
}
